import { ONE_LINK_PARAMS_MAP } from '../../../utils/marketingHelper';

/** URL params BI wants on every Mixpanel event (PLT-911, PLT-964). OneLink aliases (`deep_link_sub2`, …) count too. */
export const URL_ATTRIBUTION_PARAMS = [
  'landing',
  'utm_content_id',
  'utm_id',
  'utm_promo',
  'utm_affiliate_ad',
  'ScCid',
  'rdt_cid',
  'twclid',
  'corid',
  'ttclid',
  'pc',
  'utm_source',
  'utm_campaign',
  'utm_feature',
  'deep_link_value',
  'gclid',
  'fbclid',
  'msclkid',
  'utm_medium',
  'utm_content',
  'utm_term',
  'deal_id',
  'click_id',
  'tblci',
  'd10x_link_id',
  'd10x_link_name',
];

/** Sent under another name: events already carry a UI `click_id` (the button id), which would overwrite it. */
const RENAMED_PARAMS: Record<string, string> = { click_id: 'url_click_id' };

/** Mixpanel cuts strings at this length, so a longer value (a TikTok click ID is ~350 chars) goes out as `ttclid`, `ttclid_2`, … */
const MIXPANEL_MAX_STRING_LENGTH = 255;

/** The query string's values exactly as sent, without URL decoding (BI matches them to Segment and affiliate postbacks). */
function getRawQueryParams(): Map<string, string> {
  const params = new Map<string, string>();
  for (const pair of window.location.search.slice(1).split('&')) {
    const [key, ...rest] = pair.split('=');
    if (key && !params.has(key)) params.set(key, rest.join('='));
  }
  return params;
}

/** The listed params present in the current URL, raw. */
export function getUrlAttributionProps(): Record<string, string> {
  const urlParams = getRawQueryParams();
  const props: Record<string, string> = {};
  for (const param of URL_ATTRIBUTION_PARAMS) {
    const value = urlParams.get(param) || urlParams.get(ONE_LINK_PARAMS_MAP[param]);
    if (!value) continue;
    const name = RENAMED_PARAMS[param] ?? param;
    for (let part = 0; part * MIXPANEL_MAX_STRING_LENGTH < value.length; part++) {
      const start = part * MIXPANEL_MAX_STRING_LENGTH;
      props[part === 0 ? name : `${name}_${part + 1}`] = value.slice(start, start + MIXPANEL_MAX_STRING_LENGTH);
    }
  }
  return props;
}

/** The current URL came from an AppsFlyer OneLink: its web fallback carries `deep_link_value` / `deep_link_sub*`. */
export function isOneLinkUrl(): boolean {
  return [...new URLSearchParams(window.location.search).keys()].some(
    (key) => key === 'deep_link_value' || /^deep_link_sub\d+$/.test(key),
  );
}

/**
 * One Mixpanel event's properties: the URL's attribution params and `is_deep_link`, then the event's own (which
 * win). `$current_url` is set here, as Segment does, because the SDK would read it only when its queue drains.
 */
export function withUrlAttribution(params: Record<string, unknown>): Record<string, unknown> {
  return {
    $current_url: window.location.href,
    ...getUrlAttributionProps(),
    ...(isOneLinkUrl() ? { is_deep_link: true } : {}),
    ...params,
  };
}
